import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api, { ForecastResponse } from '../../api';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import { renderMarkdown } from '../../utils/renderMarkdown';
import { cardStyle, CardDecorations, headingStyle, spinnerStyle, CollapsibleCard } from './dashboardStyles';

type AreaKey = 'career' | 'love' | 'health';

interface AreaTab {
  key: AreaKey;
  label: string;
  icon: string;
  color: string;
}

const AREA_TABS: AreaTab[] = [
  { key: 'career', label: 'Работа', icon: '💼', color: '#4CAF50' },
  { key: 'love', label: 'Любовь', icon: '❤️', color: '#E91E63' },
  { key: 'health', label: 'Здоровье', icon: '🏥', color: '#FF9800' },
];

const DashboardDailyForecast: React.FC = () => {
  const { user } = useAuth();
  const [forecast, setForecast] = useState<string>('');
  const [areaForecast, setAreaForecast] = useState<string>('');
  const [activeArea, setActiveArea] = useState<AreaKey | null>(null);
  const [loading, setLoading] = useState(true);
  const [areaLoading, setAreaLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchForecast = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        const res = await api.get<ForecastResponse>(`/calculations/forecast/daily/${user.id}`);
        if (res.success && res.data) {
          const text = res.data.aiInterpretation || res.data.forecast || res.data.answer || res.data.summary || '';
          setForecast(text);
        }
      } catch {
        // Silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchForecast();
  }, [user?.id]);

  const handleAreaClick = useCallback(async (area: AreaKey) => {
    if (!user?.id) return;
    setActiveArea(area);
    setAreaLoading(true);
    setAreaForecast('');
    try {
      const res = await api.get<ForecastResponse>(`/calculations/forecast/area/${user.id}/${area}`);
      if (res.success && res.data) {
        const text = res.data.aiInterpretation || res.data.forecast || res.data.answer || res.data.summary || '';
        setAreaForecast(text);
      }
    } catch {
      setAreaForecast('Не удалось загрузить прогноз для этой сферы.');
    } finally {
      setAreaLoading(false);
    }
  }, [user?.id]);

  const displayText = activeArea ? (areaLoading ? '' : areaForecast) : forecast;

  return (
    <CollapsibleCard title="🔮 Прогноз на сегодня" marginBottom="24px" padding={isMobile ? '16px 12px' : '20px'}>
      <div>
        {loading ? (
          <div style={spinnerStyle}>✨ Загрузка прогноза...</div>
        ) : !forecast && !activeArea ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ color: '#888', fontSize: '14px', marginBottom: '16px' }}>
              Прогноз на сегодня ещё не готов
            </p>
            <button
              onClick={() => {
                if (user?.id) {
                  setLoading(true);
                  api.get<ForecastResponse>(`/calculations/forecast/daily/${user.id}`).then(res => {
                    if (res.success && res.data) {
                      const text = res.data.aiInterpretation || res.data.forecast || res.data.answer || res.data.summary || '';
                      setForecast(text);
                    }
                  }).catch(() => {}).finally(() => setLoading(false));
                }
              }}
              style={{
                background: 'linear-gradient(135deg, #d4af37, #b8942b)',
                color: '#1a1a2e',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 20px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Получить прогноз на сегодня
            </button>
          </div>
        ) : (
          <>
            <div
              style={{
                color: '#e0e0e0',
                fontSize: '14px',
                lineHeight: 1.7,
                marginBottom: '20px',
                minHeight: '60px',
              }}
              dangerouslySetInnerHTML={{
                __html: displayText
                  ? sanitizeHtml(renderMarkdown(displayText))
                  : '<span style="color:#888">Загрузка...</span>',
              }}
            />
            {areaLoading && (
              <div style={{ color: '#d4af37', fontSize: '13px', textAlign: 'center', padding: '10px' }}>
                Загрузка...
              </div>
            )}
          </>
        )}

        {/* Area tabs */}
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
          marginTop: '16px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          paddingTop: '16px',
        }}>
          {AREA_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleAreaClick(tab.key)}
              style={{
                background: activeArea === tab.key ? `${tab.color}22` : 'rgba(255,255,255,0.04)',
                border: `1px solid ${activeArea === tab.key ? tab.color : 'rgba(255,255,255,0.1)'}`,
                borderRadius: '10px',
                padding: '8px 16px',
                color: activeArea === tab.key ? tab.color : '#b0b0b0',
                fontSize: '13px',
                fontWeight: activeArea === tab.key ? 600 : 400,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
              onMouseEnter={(e) => {
                if (activeArea !== tab.key) {
                  e.currentTarget.style.borderColor = `${tab.color}66`;
                  e.currentTarget.style.color = tab.color;
                }
              }}
              onMouseLeave={(e) => {
                if (activeArea !== tab.key) {
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                  e.currentTarget.style.color = '#b0b0b0';
                }
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default DashboardDailyForecast;
